import React from "react";
import { RefreshCw, Brain, Check, X, Mic, Headphones, Radio, Square, Trash2, Pencil, RotateCcw } from "lucide-react";
import { StatusPill, isScheduled, fmtSlot } from "./PodcastEpCard";

export function PodcastDetailPanel({
  activeEp, dm, c, btnPrimary, btnSecondary, inputCls, labelCls,
  editedScript, onScriptChange,
  loadingScript,
  refineInstruction, onRefineChange,
  refining, approving, rejecting,
  recordingEpId, publishingEpId, pollingScript,
  elModel,
  audioUrl, loadingAudio,
  editedTitle, onTitleChange, onTitleBlur, savingTitle,
  editedDescription, onDescChange, onDescBlur, savingDescription, generatingDesc, onGenerateDesc,
  onClose, onApprove, onReject, onRefine, onGenerateAudio, onPublish, onLoadAudio,
  onDelete,
}) {
  if (!activeEp) return null;
  const isPublished = activeEp.status === "published";

  return (
    <div className={`rounded-2xl border ${c.border} ${c.card} p-4 space-y-4`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          {isPublished ? (
            /* Published — title read-only */
            <p className={`text-sm font-bold truncate ${c.text}`}>{activeEp.title || "Episode"}</p>
          ) : (
            /* Editable title */
            <div className="relative group">
              <input
                data-testid="episode-title-input"
                value={editedTitle}
                onChange={e => onTitleChange(e.target.value)}
                onBlur={e => onTitleBlur(e.target.value)}
                onKeyDown={e => e.key === "Enter" && e.target.blur()}
                className={`w-full text-sm font-bold bg-transparent border-0 border-b outline-none pr-5 pb-0.5 truncate transition-colors
                  ${dm
                    ? "text-slate-100 border-slate-700 focus:border-cyan-400 placeholder:text-slate-600"
                    : "text-[#191c1d] border-[#e0e0e0] focus:border-[#006874] placeholder:text-[#bac9cc]"
                  }`}
                placeholder="Episode title…"
              />
              {savingTitle
                ? <RefreshCw className={`absolute right-0 top-0.5 w-3 h-3 animate-spin ${c.muted}`} />
                : <Pencil className={`absolute right-0 top-0.5 w-3 h-3 opacity-0 group-hover:opacity-40 transition-opacity ${c.muted}`} />
              }
            </div>
          )}
          {activeEp.week && <p className={`text-[11px] mt-0.5 ${c.muted}`}>{activeEp.week}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <StatusPill status={activeEp.status} ep={activeEp} />
          <button
            onClick={() => {
              if (window.confirm("Delete this episode? This will also remove it from Spreaker if published there.")) {
                onDelete(activeEp.id);
              }
            }}
            className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium text-red-400 border border-red-400/20 hover:bg-red-400/10 transition-colors"
            title="Delete episode"
            data-testid="delete-episode-btn"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onClose}
            className={`p-1 rounded-lg ${dm ? "hover:bg-white/5" : "hover:bg-[#f3f4f5]"}`}
          >
            <X className={`w-4 h-4 ${c.muted}`} />
          </button>
        </div>
      </div>

      {/* Episode Description */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <label className={labelCls}>Description</label>
          {activeEp.status !== "published" && (
            <button
              onClick={onGenerateDesc}
              disabled={generatingDesc}
              data-testid="generate-desc-btn"
              className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border transition-colors disabled:opacity-40
                ${dm
                  ? "border-cyan-400/20 text-cyan-400 hover:bg-cyan-400/10"
                  : "border-[#006874]/20 text-[#006874] hover:bg-[#006874]/8"
                }`}
            >
              {generatingDesc
                ? <><RefreshCw className="w-2.5 h-2.5 animate-spin" />Generating…</>
                : <><RotateCcw className="w-2.5 h-2.5" />Regenerate</>
              }
            </button>
          )}
        </div>
        {activeEp.status === "published" ? (
          <p className={`text-xs leading-relaxed ${c.muted}`}>{editedDescription || activeEp.description || "—"}</p>
        ) : (
          <div className="relative">
            <textarea
              rows={3}
              value={editedDescription}
              onChange={e => onDescChange(e.target.value)}
              onBlur={e => onDescBlur(e.target.value)}
              className={`${inputCls} resize-none text-xs leading-relaxed`}
              placeholder="AI-generated description will appear here…"
              data-testid="episode-description-textarea"
            />
            {savingDescription && (
              <RefreshCw className={`absolute right-2 bottom-2 w-3 h-3 animate-spin ${c.muted}`} />
            )}
          </div>
        )}
      </div>

      {activeEp.status !== "published" && (
        <div className="space-y-2">
          <label className={labelCls}>Script (spoken text only — ready for TTS)</label>
          {loadingScript ? (
            <div className={`h-48 rounded-xl flex items-center justify-center ${dm ? "bg-[#1a2324]" : "bg-[#f5f6f7]"}`}>
              <RefreshCw className={`w-5 h-5 animate-spin ${c.muted}`} />
            </div>
          ) : (
            <textarea
              rows={14}
              value={editedScript}
              onChange={e => onScriptChange(e.target.value)}
              disabled={!["draft","pending_approval","approved"].includes(activeEp.status)}
              className={`${inputCls} resize-y font-mono text-xs leading-relaxed`}
              data-testid="script-editor"
              placeholder="Script will appear here after generation…"
            />
          )}

          {/* AI Refine */}
          {["draft","pending_approval","approved"].includes(activeEp.status) && (
            <div className="flex gap-2">
              <input
                type="text"
                value={refineInstruction}
                onChange={e => onRefineChange(e.target.value)}
                placeholder="Refine with AI — e.g. 'Cut 20%' or 'More upbeat ending'"
                className={inputCls}
                data-testid="refine-instruction-input"
              />
              <button
                onClick={() => onRefine(activeEp.id)}
                disabled={refining || !refineInstruction.trim()}
                className={btnSecondary}
              >
                {refining
                  ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  : <Brain className="w-3.5 h-3.5" />}
                Refine
              </button>
            </div>
          )}

          {/* Approve / Reject */}
          {["draft","pending_approval"].includes(activeEp.status) && (
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => onApprove(activeEp.id)}
                disabled={approving || loadingScript}
                className={`flex-1 justify-center ${btnPrimary}`}
                data-testid="approve-script-btn"
              >
                {approving
                  ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  : <Check className="w-3.5 h-3.5" />}
                Approve Script
              </button>
              <button
                onClick={() => onReject(activeEp.id)}
                disabled={rejecting}
                className={`${btnSecondary} text-red-400 border-red-400/20`}
              >
                <X className="w-3.5 h-3.5" /> Reject
              </button>
            </div>
          )}

          {/* Record button (for approved episodes) */}
          {["approved","audio_error"].includes(activeEp.status) && (
            <button
              onClick={() => onGenerateAudio(activeEp.id)}
              disabled={!!recordingEpId || pollingScript}
              className={`w-full justify-center ${btnPrimary}`}
              data-testid="generate-audio-detail-btn"
            >
              {recordingEpId === activeEp.id
                ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                : <Mic className="w-3.5 h-3.5" />}
              {recordingEpId === activeEp.id
                ? "Recording… (polling)"
                : `Record Audio (${elModel} · max 15 min)`}
            </button>
          )}
        </div>
      )}

      {/* Audio player — shown when audio is ready or published */}
      {["audio_ready","publish_error","published"].includes(activeEp.status) && (
        <div className="space-y-2">
          <label className={labelCls}>Audio preview</label>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => onLoadAudio(activeEp.id)}
              disabled={loadingAudio}
              className={btnSecondary}
              data-testid="load-audio-btn"
            >
              {loadingAudio
                ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                : (audioUrl ? <Square className="w-3.5 h-3.5" /> : <Headphones className="w-3.5 h-3.5" />)}
              {audioUrl ? "Hide player" : "Load audio"}
            </button>
            {["audio_ready","publish_error"].includes(activeEp.status) && (
              <>
                <button
                  onClick={() => onPublish(activeEp.id)}
                  disabled={!!publishingEpId}
                  className={`flex-1 justify-center ${btnPrimary}`}
                  data-testid="publish-detail-btn"
                >
                  {publishingEpId === activeEp.id
                    ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    : <Radio className="w-3.5 h-3.5" />}
                  {publishingEpId === activeEp.id ? "Publishing…" : "Publish to Spreaker"}
                </button>
                <button
                  onClick={() => {
                    if (window.confirm("Re-record audio with current model and voice settings? The existing audio will be overwritten.")) {
                      onGenerateAudio(activeEp.id);
                    }
                  }}
                  disabled={!!recordingEpId || pollingScript}
                  className={`${btnSecondary} text-amber-400 border-amber-400/20`}
                  title={`Re-record with ${elModel} and current voice parameters`}
                  data-testid="rerecord-audio-btn"
                >
                  {recordingEpId === activeEp.id
                    ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    : <Mic className="w-3.5 h-3.5" />}
                  Re-record
                </button>
              </>
            )}
          </div>
          {audioUrl && (
            <audio controls src={audioUrl} className="w-full mt-1" style={{ height: 40 }}>
              Your browser does not support audio.
            </audio>
          )}
        </div>
      )}

      {/* Published confirmation */}
      {activeEp.status === "published" && (
        <div className={`rounded-xl p-3 ${dm
          ? "bg-emerald-500/10 border border-emerald-500/20"
          : "bg-emerald-50 border border-emerald-200"}`}
        >
          <p className="text-xs font-bold text-emerald-500 mb-1">
            {isScheduled(activeEp) ? "Uploaded to Spreaker — scheduled" : "Published on Spreaker"}
          </p>
          {activeEp.publish_mode === "scheduled" && activeEp.published_at && (
            <p className={`text-[11px] mb-1 ${dm ? "text-emerald-200/80" : "text-emerald-800"}`} data-testid="podcast-scheduled-detail">
              {isScheduled(activeEp) ? "Goes live" : "Went live"} {fmtSlot(activeEp.published_at)} Europe/Rome — aligned to the Italian edition (Tue 06:30); the English episode never goes live first.
            </p>
          )}
          {activeEp.spreaker_episode_url && (
            <a
              href={activeEp.spreaker_episode_url}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-emerald-400 underline"
            >
              {activeEp.spreaker_episode_url}
            </a>
          )}
        </div>
      )}
    </div>
  );
}
